import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboarding, type OnboardingIntent } from './OnboardingProvider';
import { MaterialSymbol } from '../icons/MaterialSymbol';

const ICON_PROPS = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const JobIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" {...ICON_PROPS}>
    <path d="M8.5 6.5C8.5 5.09554 8.5 4.39331 8.83706 3.88886C8.98298 3.67048 9.17048 3.48298 9.38886 3.33706C9.89331 3 10.5955 3 12 3C13.4045 3 14.1067 3 14.6111 3.33706C14.8295 3.48298 15.017 3.67048 15.1629 3.88886C15.5 4.39331 15.5 5.09554 15.5 6.5" />
    <path d="M22 14V13.5C22 10.2002 22 8.55025 20.9749 7.52513C19.9497 6.5 18.2998 6.5 15 6.5H9C5.70017 6.5 4.05025 6.5 3.02513 7.52513C2 8.55025 2 10.2002 2 13.5V14C2 17.2998 2 18.9497 3.02513 19.9749C4.05025 21 5.70017 21 9 21H15C18.2998 21 19.9497 21 20.9749 19.9749C22 18.9497 22 17.2998 22 14Z" />
    <path d="M2 11C2 11 4.63158 15 12 15C19.3684 15 22 11 22 11" />
    <path d="M12.125 11.75H12M12.25 11.75C12.25 11.8881 12.1381 12 12 12C11.8619 12 11.75 11.8881 11.75 11.75C11.75 11.6119 11.8619 11.5 12 11.5C12.1381 11.5 12.25 11.6119 12.25 11.75Z" />
  </svg>
);

const FreelancerIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" {...ICON_PROPS}>
    <path d="M14.9263 4.41103L8.27352 7.60452C7.76151 7.85029 7.21443 7.91187 6.65675 7.78693C6.29177 7.70517 6.10926 7.66429 5.9623 7.64751C4.13743 7.43912 3 8.88342 3 10.5443V11.4557C3 13.1166 4.13743 14.5609 5.9623 14.3525C6.10926 14.3357 6.29178 14.2948 6.65675 14.2131C7.21443 14.0881 7.76151 14.1497 8.27352 14.3955L14.9263 17.589C16.4534 18.3221 17.217 18.6886 18.0684 18.4029C18.9197 18.1172 19.2119 17.5041 19.7964 16.278C21.4012 12.9112 21.4012 9.08885 19.7964 5.72196C19.2119 4.49586 18.9197 3.88281 18.0684 3.5971C17.217 3.3114 16.4534 3.67794 14.9263 4.41103Z" />
    <path d="M13 17V17.5C13 18.7841 13 19.4261 12.776 19.7886C12.4773 20.2719 11.9312 20.545 11.3653 20.4939C10.9409 20.4557 10.4273 20.0704 9.4 19.3L8.2 18.4C7.22253 17.6669 7 17.2218 7 16V14.5" />
    <path d="M7.5 14V8" />
  </svg>
);

const CompanyIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" {...ICON_PROPS}>
    <path d="M16 10L18.1494 10.6448C19.5226 11.0568 20.2092 11.2628 20.6046 11.7942C21 12.3256 21 13.0425 21 14.4761V22" />
    <path d="M8 9L11 9M8 13L11 13" />
    <path d="M12 22V19C12 18.0572 12 17.5858 11.7071 17.2929C11.4142 17 10.9428 17 10 17H9C8.05719 17 7.58579 17 7.29289 17.2929C7 17.5858 7 18.0572 7 19V22" />
    <path d="M2 22L22 22" />
    <path d="M3 22V6.71724C3 4.20649 3 2.95111 3.79118 2.32824C4.58237 1.70537 5.74742 2.04355 8.07752 2.7199L13.0775 4.17122C14.4836 4.57937 15.1867 4.78344 15.5933 5.33965C16 5.89587 16 6.65344 16 8.16857V22" />
  </svg>
);

const OPTIONS: {
  id: OnboardingIntent;
  Icon: FC;
  title: string;
  desc: string;
  titleKey: string;
  descKey: string;
}[] = [
  {
    id: 'job',
    Icon: JobIcon,
    title: 'Looking for a job',
    desc: 'Build your CV and discover opportunities',
    titleKey: 'onboarding.intent.lookingForJob',
    descKey: 'onboarding.intent.lookingForJobDesc',
  },
  {
    id: 'freelancer',
    Icon: FreelancerIcon,
    title: 'Freelancer',
    desc: 'Find clients and showcase your services',
    titleKey: 'onboarding.intent.freelancer',
    descKey: 'onboarding.intent.freelancerDesc',
  },
  {
    id: 'company',
    Icon: CompanyIcon,
    title: 'Company',
    desc: 'Build your career page and hire top talent',
    titleKey: 'onboarding.intent.company',
    descKey: 'onboarding.intent.companyDesc',
  },
];

export function StepIntentSelect() {
  const { t } = useTranslation('auth');
  const { intent, selectIntentAndAdvance, isIntentTransitioning } = useOnboarding();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleSelect = (id: OnboardingIntent) => {
    selectIntentAndAdvance(id);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: '40px 0 48px',
      }}
    >
      <div
        style={{
          maxWidth: 560,
          marginInline: 'auto',
          width: '100%',
        }}
      >
        <h1
          style={{
            fontFamily: "'Google Sans', sans-serif",
            fontSize: 32,
            fontWeight: 400,
            lineHeight: '40px',
            letterSpacing: '-0.01em',
            color: 'var(--text-primary)',
            margin: '0 0 8px',
          }}
        >
          {t('onboarding.intent.title', {
            defaultValue: 'What brings you here?',
          })}
        </h1>
        <p
          style={{
            fontSize: 15,
            lineHeight: '22px',
            color: 'var(--text-secondary)',
            margin: '0 0 32px',
          }}
        >
          {t('onboarding.intent.subtitle', {
            defaultValue: 'This shapes your experience.',
          })}
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {OPTIONS.map((opt) => {
            const isSelected = intent === opt.id;
            const isHovered = hoveredId === opt.id;
            const isLifted = isSelected || isHovered;
            const Icon = opt.Icon;
            return (
              <button
                type="button"
                key={opt.id}
                onClick={() => handleSelect(opt.id)}
                disabled={isIntentTransitioning}
                onMouseEnter={() => setHoveredId(opt.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '18px 20px',
                  borderRadius: 20,
                  border: 0,
                  backgroundColor: isLifted
                    ? 'rgba(0,0,0,0.06)'
                    : 'rgba(0,0,0,0.04)',
                  cursor: isIntentTransitioning ? 'default' : 'pointer',
                  textAlign: 'start' as const,
                  fontFamily: 'inherit',
                  opacity: isIntentTransitioning ? 0.6 : 1,
                  transition: 'background-color 150ms',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'var(--text-secondary)',
                  }}
                >
                  <Icon />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      lineHeight: '20px',
                    }}
                  >
                    {t(opt.titleKey, { defaultValue: opt.title })}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: '18px',
                      color: 'var(--text-secondary)',
                      marginTop: 4,
                    }}
                  >
                    {t(opt.descKey, { defaultValue: opt.desc })}
                  </div>
                </div>
                <MaterialSymbol
                  name="chevron_right"
                  size={20}
                  style={{
                    color: 'var(--text-tertiary)',
                    flexShrink: 0,
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
